import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  Grid,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';

const RentalSettings = () => {
  const { user } = useAuth();
  const companyId = user?.companyId;
  
  const [settings, setSettings] = useState({
    dailyPrice: '',
    weeklyPrice: '',
    monthlyPrice: ''
  });
  
  const [discountLevels, setDiscountLevels] = useState([
    { numeroVeicoli: 2, percentuale: 10 }
  ]);
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const res = await api.get('/company-settings');
        
        // Le impostazioni vengono restituite come array di oggetti {key, value} o come oggetto
        let rentalSettings = {};
        
        if (Array.isArray(res.data)) {
          // Se è un array, convertiamo in oggetto
          res.data.forEach(setting => {
            if (setting.key && setting.value) {
              rentalSettings[setting.key] = setting.value;
            }
          });
        } else if (res.data) {
          // Se è un oggetto, usiamo direttamente
          rentalSettings = res.data;
        }
        
        setSettings({
          dailyPrice: rentalSettings.rentalDailyPrice || '25.00',
          weeklyPrice: rentalSettings.rentalWeeklyPrice || '20.00',
          monthlyPrice: rentalSettings.rentalMonthlyPrice || '11.67'
        });
        
        // Carica i livelli di sconto
        let levels = [];
        if (rentalSettings.rentalDiscountLevels) {
          try {
            levels = JSON.parse(rentalSettings.rentalDiscountLevels);
          } catch (e) {
            logger.error('Error parsing discount levels:', e);
            levels = [{ numeroVeicoli: 2, percentuale: 10 }];
          }
        } else {
          // Valore di default se non esistono livelli salvati
          levels = [{ numeroVeicoli: 2, percentuale: 10 }];
        }
        setDiscountLevels(levels);
      } catch (err) {
        logger.error('Error fetching rental settings:', err);
        setError('Errore nel caricamento delle impostazioni');
        // Valori di default
        setSettings({
          dailyPrice: '25.00',
          weeklyPrice: '20.00',
          monthlyPrice: '11.67'
        });
        setDiscountLevels([{ numeroVeicoli: 2, percentuale: 10 }]);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [companyId]);

  const handleChange = (field) => (e) => {
    setSettings({
      ...settings,
      [field]: e.target.value
    });
    setError(null);
    setSuccess(false);
  };
  
  const handleDiscountLevelChange = (index, field) => (e) => {
    const newLevels = [...discountLevels];
    newLevels[index] = {
      ...newLevels[index],
      [field]: e.target.value
    };
    setDiscountLevels(newLevels);
    setError(null);
    setSuccess(false);
  };
  
  const handleAddDiscountLevel = () => {
    const maxVeicoli = discountLevels.length > 0 
      ? Math.max(...discountLevels.map(l => parseInt(l.numeroVeicoli) || 0))
      : 1;
    setDiscountLevels([
      ...discountLevels,
      { numeroVeicoli: maxVeicoli + 2, percentuale: 0 }
    ]);
  };
  
  const handleRemoveDiscountLevel = (index) => {
    if (discountLevels.length > 1) {
      const newLevels = discountLevels.filter((_, i) => i !== index);
      setDiscountLevels(newLevels);
    } else {
      setError('Devi avere almeno un livello di sconto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validazione
    if (!settings.dailyPrice || parseFloat(settings.dailyPrice) < 0) {
      setError('Il prezzo giornaliero deve essere un numero positivo');
      return;
    }
    if (!settings.weeklyPrice || parseFloat(settings.weeklyPrice) < 0) {
      setError('Il prezzo settimanale deve essere un numero positivo');
      return;
    }
    if (!settings.monthlyPrice || parseFloat(settings.monthlyPrice) < 0) {
      setError('Il prezzo mensile deve essere un numero positivo');
      return;
    }
    
    // Validazione livelli di sconto
    if (discountLevels.length === 0) {
      setError('Devi avere almeno un livello di sconto');
      return;
    }
    
    for (let i = 0; i < discountLevels.length; i++) {
      const level = discountLevels[i];
      if (!level.numeroVeicoli || parseInt(level.numeroVeicoli) < 2) {
        setError(`Il numero di veicoli al livello ${i + 1} deve essere almeno 2`);
        return;
      }
      if (!level.percentuale || parseFloat(level.percentuale) < 0 || parseFloat(level.percentuale) > 100) {
        setError(`La percentuale di sconto al livello ${i + 1} deve essere tra 0 e 100`);
        return;
      }
      // Verifica che i livelli siano ordinati per numero di veicoli crescente
      if (i > 0 && parseInt(level.numeroVeicoli) <= parseInt(discountLevels[i - 1].numeroVeicoli)) {
        setError(`Il numero di veicoli deve essere crescente (livello ${i + 1} deve essere maggiore del livello ${i})`);
        return;
      }
    }

    try {
      setLoading(true);
      
      // Salva le impostazioni usando il sistema key-value di CompanySettings
      const settingsToSave = {
        rentalDailyPrice: parseFloat(settings.dailyPrice).toFixed(2),
        rentalWeeklyPrice: parseFloat(settings.weeklyPrice).toFixed(2),
        rentalMonthlyPrice: parseFloat(settings.monthlyPrice).toFixed(2),
        rentalDiscountLevels: JSON.stringify(discountLevels.map(level => ({
          numeroVeicoli: parseInt(level.numeroVeicoli),
          percentuale: parseFloat(level.percentuale)
        })))
      };

      // Usiamo PATCH per aggiornare le impostazioni
      await api.patch('/company-settings', settingsToSave);
      
      setSuccess(true);
      
      // Notifica altri componenti che le impostazioni sono state aggiornate
      window.dispatchEvent(new CustomEvent('rentalSettingsUpdated'));
      
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      logger.error('Error saving rental settings:', err);
      setError('Errore nel salvataggio delle impostazioni: ' + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Impostazioni Noleggio
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configura i prezzi e gli sconti per i noleggi
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Prezzi Noleggio */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Prezzi Noleggio
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Prezzo Giornaliero"
                  type="number"
                  value={settings.dailyPrice}
                  onChange={handleChange('dailyPrice')}
                  fullWidth
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  helperText="Prezzo per noleggio giornaliero"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Prezzo Settimanale"
                  type="number"
                  value={settings.weeklyPrice}
                  onChange={handleChange('weeklyPrice')}
                  fullWidth
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  helperText="Prezzo per noleggio settimanale"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Prezzo Mensile"
                  type="number"
                  value={settings.monthlyPrice}
                  onChange={handleChange('monthlyPrice')}
                  fullWidth
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    inputProps: { min: 0, step: 0.01 }
                  }}
                  helperText="Prezzo per noleggio mensile"
                />
              </Grid>

              {/* Sconti Noleggio Multiplo */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Sconto Noleggio Multiplo
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddDiscountLevel}
                    disabled={loading}
                  >
                    Aggiungi Livello
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {discountLevels.map((level, index) => (
                <Grid item xs={12} key={index}>
                  <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Numero Veicoli"
                          type="number"
                          value={level.numeroVeicoli}
                          onChange={handleDiscountLevelChange(index, 'numeroVeicoli')}
                          fullWidth
                          required
                          disabled={loading}
                          InputProps={{
                            inputProps: { min: 2, step: 1 }
                          }}
                          helperText={`Minimo ${level.numeroVeicoli} veicoli`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Percentuale Sconto"
                          type="number"
                          value={level.percentuale}
                          onChange={handleDiscountLevelChange(index, 'percentuale')}
                          fullWidth
                          required
                          disabled={loading}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 100, step: 0.01 }
                          }}
                          helperText={`Sconto ${level.percentuale}%`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            {level.numeroVeicoli >= 2 ? `Da ${level.numeroVeicoli} veicoli: ${level.percentuale}%` : 'Configura il livello'}
                          </Typography>
                          {discountLevels.length > 1 && (
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveDiscountLevel(index)}
                              disabled={loading}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}

              {/* Bottoni */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    Salva Impostazioni
                  </Button>
                  {error && <Alert severity="error">{error}</Alert>}
                  {success && (
                    <Alert severity="success">
                      Impostazioni salvate con successo
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RentalSettings;
